var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { t } from '@lingui/core/macro';
import { mergeArrayByField } from 'shared/domains/apollo/cache/mergeArrayByField';
import { metadataCategories } from '../utils/metadataCategories';
import { metadataConfig } from '../utils/metadataConfig';
export const documentMetadataPropertyPolicy = {
    fields: {
        /**
         * Client-only field that resolves a `MetadataCategory` from the local category configuration.
         *
         * Looks up the category by `categoryId` on the parent `DocumentMetadataProperty`.
         * The `categoryId` field must be included in the operation alongside `category @client`,
         * otherwise the resolver will return `null`.
         *
         * @client directive has to be used when querying the field
         */
        category: {
            read(_, { readField }) {
                const categoryId = readField('categoryId') || '';
                const category = metadataCategories().get(categoryId);
                return category !== null && category !== void 0 ? category : null;
            },
        },
        /**
         * Automatically translates labels for system metadata properties.
         *
         * Custom properties use the label from the database since they are user-created.
         *
         * Note: We pass `existing` (the label) to `metadataConfig.get()` instead of reading
         * the parent `name` field. This works because for system properties, `name` and `label`
         * are identical. This approach avoids requiring the `name` field in every query.
         */
        label: {
            read(existing) {
                const fallback = t({
                    message: 'Unknown property',
                    comment: 'Metadata: Unknown property',
                });
                const { label } = metadataConfig.get(existing || '') || {};
                return label || existing || fallback;
            },
        },
        allowedValues: {
            /**
             * Adds unique identifiers to allowed values that contain grouped sub-values.
             *
             * The API returns parent group items with empty string values, but we need proper
             * identifiers for localization. For grouped values (e.g., countries with states/regions),
             * we extract the parent's value from the first grouped item without a "|" separator.
             *
             * Example: "United Kingdom" has groupValues like "GB|EAW", "GB|SCT", "GB|NIR", and "GB".
             * We extract "GB" (no "|") and set it as the parent's value for proper identification.
             */
            merge(_, incoming) {
                return incoming === null || incoming === void 0 ? void 0 : incoming.map((allowedValue) => {
                    const { groupValues } = allowedValue, rest = __rest(allowedValue, ["groupValues"]);
                    let { value } = allowedValue;
                    if (!value.length && groupValues) {
                        const [groupValue] = groupValues;
                        const [gvIdentifier] = groupValue.value.split('|');
                        value = `CLIENT|${gvIdentifier}`;
                    }
                    return Object.assign(Object.assign({}, rest), { value,
                        groupValues });
                });
            },
            /**
             * Translates text fields for system metadata properties.
             *
             * Applies translations from metadataConfig to both top-level allowed values
             * and their nested groupValues, while preserving all Apollo cache metadata.
             */
            read(existing, { readField }) {
                const name = readField('name');
                const { allowedValues } = metadataConfig.get(name || '') || {};
                if (!allowedValues) {
                    return existing ? [...existing] : [];
                }
                const existingData = existing ? [...existing] : [];
                const translateTextField = (item, translations) => {
                    const { value, text, groupValues } = item || {};
                    const translatedAllowedValue = translations.find((av) => value.length ? av.value === value : av.text === text);
                    return Object.assign(Object.assign({}, item), { text: (translatedAllowedValue === null || translatedAllowedValue === void 0 ? void 0 : translatedAllowedValue.text) || text, value, groupValues: [...(groupValues || [])].map((gv) => {
                            var _a;
                            const translatedGroupValue = (_a = translatedAllowedValue === null || translatedAllowedValue === void 0 ? void 0 : translatedAllowedValue.groupValues) === null || _a === void 0 ? void 0 : _a.find((av) => av.value === gv.value);
                            return Object.assign(Object.assign({}, gv), { text: (translatedGroupValue === null || translatedGroupValue === void 0 ? void 0 : translatedGroupValue.text) || gv.text });
                        }) });
                };
                return existingData.map((item) => translateTextField(item, allowedValues));
            },
        },
    },
};
export const typePolicies = {
    Query: {
        fields: {
            documentMetadataAiAutoTaggingQueueEntries: {
                merge: mergeArrayByField('documentId'),
            },
            metadataCategories: {
                read() {
                    return metadataCategories().getAll();
                },
            },
        },
    },
    DocumentMetadataProperty: documentMetadataPropertyPolicy,
    AvailableDocumentMetadataCustomPropertyCountResponse: {
        keyFields: ['name'],
    },
};
